import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import FlameMark from "@/components/FlameMark";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { ArrowRight, AlertTriangle, PhoneCall, Smartphone, FileSignature } from "lucide-react";

const APP_VERSION = process.env.REACT_APP_APP_VERSION;
const SUPPORT_EMAIL = "support@blueflames.example";
const FEATURES = [
  [PhoneCall, "Log a service call in under a minute"],
  [Smartphone, "Technicians start, pause and complete jobs from their phone"],
  [FileSignature, "Signed service reports for every visit"],
];

function Brand({ dark }) {
  return (
    <div className="flex items-center gap-3" data-testid={dark ? "login-brand-desktop" : "login-brand-mobile"}>
      <FlameMark size={dark ? 56 : 48} />
      <div className="leading-tight">
        <div className={`font-semibold tracking-tight ${dark ? "text-[20px]" : "text-[18px]"}`}
             style={{ color: dark ? "#FFFFFF" : "#12161B" }}>Blueflames Service Pro</div>
        <div className="text-[12.5px] mt-1" style={{ color: dark ? "#8FB7F0" : "#5F6B7A" }}>
          Commercial kitchen equipment service · Barbados
        </div>
      </div>
    </div>
  );
}

function BrandPanel() {
  return (
    <div className="hidden md:flex md:w-1/2 relative overflow-hidden"
         style={{ background: "linear-gradient(160deg, #0F1E33 0%, #101820 55%, #0B2A52 100%)" }}
         data-testid="login-brand-panel">
      <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
        <Brand dark />
        <div>
          <h1 className="text-[42px] leading-tight font-semibold tracking-tight">Keep every kitchen running.</h1>
          <p className="mt-5 text-[15px] max-w-[440px]" style={{ color: "#B6C2D1", lineHeight: 1.6 }}>
            One place for service requests, dispatch, technician visits and signed job reports.
          </p>
          <ul className="mt-10 space-y-4 max-w-[460px]">
            {FEATURES.map(([Icon, text]) => (
              <li key={text} className="flex items-center gap-3" data-testid="login-feature-row">
                <span className="h-9 w-9 shrink-0 rounded-[10px] flex items-center justify-center"
                      style={{ background: "rgba(30,111,217,0.18)" }}>
                  <Icon size={17} color="#8FB7F0" />
                </span>
                <span className="text-[14px]" style={{ color: "#E2E8F0" }}>{text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="text-[11.5px]" style={{ color: "#7D8A9B" }} data-testid="login-footer">
          Food and Equipment Solutions Inc. t/a Blueflames
          {APP_VERSION && <span className="mono" data-testid="login-version"> · v{APP_VERSION}</span>}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user } = useAuth();
  const location = useLocation();
  const errorMsg = location.state?.error;

  useEffect(() => {
    if (errorMsg) {
      toast.error("Access not granted", {
        description: errorMsg,
        duration: 8000,
      });
      // clear state so refresh doesn't re-toast
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [errorMsg, location.pathname]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (user) {
    window.location.href = "/home";
    return null;
  }

  return (
    <div className="min-h-screen flex" style={{ background: "#F7F8FA" }} data-testid="login-page">
      <Toaster richColors position="top-right" />
      <BrandPanel />
      <div className="flex-1 flex items-center justify-center px-5 py-10 sm:p-8">
        <div className="w-full max-w-[400px]">
          <div className="md:hidden mb-8"><Brand /></div>
          <div className="bf-card p-6 sm:p-7" data-testid="login-card">
            <div className="mono text-[11px] uppercase tracking-widest mb-3" style={{ color: "#5F6B7A" }}>Sign in</div>
            <h2 className="text-[26px] font-semibold tracking-tight" style={{ color: "#12161B" }}>Welcome back</h2>
            <p className="text-sm mt-2" style={{ color: "#5F6B7A" }}>
              Use the Google account on your invitation.
            </p>
            {errorMsg && (
              <div className="mt-4 flex items-start gap-2 p-3.5 rounded-[14px]"
                style={{ background: "#FDECEC" }}
                data-testid="login-error-banner"
              >
                <AlertTriangle size={16} color="#B42318" className="mt-0.5 shrink-0" />
                <div className="text-[13.5px]" style={{ color: "#B42318", lineHeight: 1.5 }}>{errorMsg}</div>
              </div>
            )}
            <Button
              data-testid="google-login-btn"
              onClick={login}
              className="w-full h-11 mt-6 bf-btn-primary"
            >
              Continue with Google <ArrowRight size={16} className="ml-2" />
            </Button>
            <p className="mt-3 text-[12.5px]" style={{ color: "#5F6B7A", lineHeight: 1.5 }} data-testid="login-invite-note">
              Access is by invitation. Ask your manager to send an invite if you can't sign in.
            </p>
          </div>
          <div className="mt-5 text-[12.5px]" style={{ color: "#5F6B7A" }} data-testid="login-help">
            Need help? Contact <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium" style={{ color: "#1E6FD9" }}
              data-testid="login-support-email">{SUPPORT_EMAIL}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
